<template>
  <v-card id="edit-agent-rule">
    <v-card-title class="i-card-title-div justify-center py-4">
      <span class="g-bold g-heading-m">Edit Agent Rule</span>
    </v-card-title>

    <v-card-text class="i-card-content-div p-spanded-div mt-6 mb-4">
      <p class="g-bold g-heading-s g-text-color-black">State(s)</p>

      <v-alert
        v-if="!hasSelectedStates"
        dense
        outlined
        text
        border="left"
        class="mt-3 mb-2"
        elevation="1"
        type="info"
      >
        <span class="g-bold"
          >States selection required to update agent rule.</span
        >
      </v-alert>

      <v-autocomplete
        attach
        background-color="#0000000f"
        class="mt-4 mb-6"
        clearable
        deletable-chips
        dense
        hide-details
        :items="stateOptions"
        label="Select State(s)"
        multiple
        outlined
        :rules="[(v) => !!v.length || 'State(s) required for State rebates']"
        v-model:search-input="searchInput"
        small-chips
        v-model="targetStoreDeliveryAddressStates"
        @change="handleStatesChange"
      ></v-autocomplete>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="i-dialog-actions-div justify-end">
      <v-btn rounded text class="i-btn-text-black" @click="handleCancel">
        <strong>Cancel</strong>
      </v-btn>

      <v-btn
        rounded
        class="i-btn-block-secondary mr-4"
        @click="handleConfirm"
        :disabled="!validRule"
        :loading="updating"
      >
        <strong>Create</strong>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import { updateAgentSupplierRule } from '@/services/agencyRules'
import { buildStateOptions } from '@/utils/stateDisplay'

const ADD_ALL_ZONES_OPTION = 'Add all zones'

export default {
  props: {
    rule: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    searchInput: '',
    targetStoreDeliveryAddressStates: [],
    originalTargetStoreDeliveryAddressStates: [],
    updating: false
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['tenantCountryRegion']),
    states() {
      return buildStateOptions(this.tenantCountryRegion).map(
        (stateOption) => stateOption.value
      )
    },
    stateOptions() {
      return [
        ADD_ALL_ZONES_OPTION,
        ...buildStateOptions(this.tenantCountryRegion).map((stateOption) => ({
          text: stateOption.text,
          value: stateOption.value
        }))
      ]
    },

    hasChanges() {
      return !_.isEqual(
        this.targetStoreDeliveryAddressStates,
        this.originalTargetStoreDeliveryAddressStates
      )
    },

    hasSelectedStates() {
      return this.targetStoreDeliveryAddressStates?.length > 0
    },

    validRule() {
      return this.hasSelectedStates && this.hasChanges
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    handleStatesChange(selectedStates = []) {
      if (selectedStates.includes(ADD_ALL_ZONES_OPTION)) {
        this.targetStoreDeliveryAddressStates = [...this.states]
      } else {
        this.targetStoreDeliveryAddressStates = selectedStates.filter(
          (state) => state !== ADD_ALL_ZONES_OPTION
        )
      }
      this.searchInput = ''
    },

    handleCancel() {
      this.$emit('cancel')
    },

    handleConfirm() {
      this.updating = true

      const payload = {
        supplierId: this.selectedSupplierId,
        agentSupplierRuleId: this.rule.id,
        updateData: {
          agentEmail: this.rule.agentInfo.email,
          targetStoreDeliveryAddressStates:
            this.targetStoreDeliveryAddressStates
        }
      }

      return updateAgentSupplierRule(payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Agent Rule updated!'
          })
          this.$emit('confirm')
        })
        .catch((error) => {
          console.log(error)
          this.addNotification({
            color: 'error',
            text: 'Agent Rule could not be edited, please try again...'
          })
        })
        .finally(() => {
          this.updating = false
        })
    }
  },

  mounted() {
    this.targetStoreDeliveryAddressStates =
      this.rule.targetStoreDeliveryAddressStates.map((state) => state.state)
    this.originalTargetStoreDeliveryAddressStates = _.cloneDeep(
      this.targetStoreDeliveryAddressStates
    )
  }
}
</script>

<style lang="scss" scoped>
#edit-agent-rule {
  .p-spanded-div {
    min-height: 450px;
  }
}
</style>
